import {
  getLatestValueFromHistory,
  getSalaryDateFromHistory,
} from "../api/api";
import { normalizeEffectiveDate } from "./buildUserUpdatePayload";

/**
 * Дата из истории, с fallback на поле верхнего уровня API (*_effective_date).
 */
export const resolveFiredDate = (user) =>
  normalizeEffectiveDate(
    user?.is_fired_date ||
      getLatestValueFromHistory(user?.user_status_history, "is_fired")
        ?.effective_date ||
      getLatestValueFromHistory(user?.user_status_history, "status")
        ?.effective_date,
  );

/** @deprecated используйте resolveFiredDate */
export const resolveStatusEffectiveDate = resolveFiredDate;

export const resolveSalaryEffectiveDate = (user) =>
  normalizeEffectiveDate(
    getSalaryDateFromHistory(user?.salary_history, user?.salary) ||
      user?.salary_effective_date,
  );

export const resolveWorkHoursPerDayEffectiveDate = (user) =>
  normalizeEffectiveDate(
    getLatestValueFromHistory(
      user?.work_hours_per_day_history,
      "work_hours_per_day",
    )?.effective_date ||
      user?.work_hours_per_day_effective_date ||
      getLatestValueFromHistory(user?.work_format_history, "work_format")
        ?.effective_date,
  );

export const resolveCompanyEffectiveDateForForm = (user) =>
  normalizeEffectiveDate(
    getLatestValueFromHistory(user?.company_history, "company")?.effective_date ||
      user?.company_id_effective_date ||
      user?.company_effective_date,
  );

export const resolveDepartmentEffectiveDateForForm = (user) =>
  normalizeEffectiveDate(
    getLatestValueFromHistory(user?.user_department_history, "department")
      ?.effective_date || user?.department_effective_date,
  );

const PENDING_DATES_STORAGE_KEY = (userId) =>
  `updateUser_pending_effective_dates_${userId}`;

/** Сохраняем даты, которые бэкенд ещё не отдаёт в GET */
export const savePendingEffectiveDates = (userId, sentPayload) => {
  if (!userId || !sentPayload) return;
  const pending = {
    is_fired_date: sentPayload.is_fired_date,
    work_hours_per_day_effective_date:
      sentPayload.work_hours_per_day_effective_date,
    department_effective_date: sentPayload.department_effective_date,
  };
  const hasAny = Object.values(pending).some(Boolean);
  if (hasAny) {
    sessionStorage.setItem(
      PENDING_DATES_STORAGE_KEY(userId),
      JSON.stringify(pending),
    );
  }
};

const readPendingEffectiveDates = (userId) => {
  if (!userId) return null;
  try {
    const raw = sessionStorage.getItem(PENDING_DATES_STORAGE_KEY(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearPendingEffectiveDates = (userId) => {
  if (userId) {
    sessionStorage.removeItem(PENDING_DATES_STORAGE_KEY(userId));
  }
};

/** Дополняет formData: GET → sessionStorage (после PUT) */
export const applyPendingEffectiveDates = (userId, user, formData) => {
  const pending = readPendingEffectiveDates(userId);
  if (!pending) return formData;

  const backendHasFiredDate =
    user?.is_fired_date || (user?.user_status_history?.length ?? 0) > 0;
  const backendHasWorkHours =
    user?.work_hours_per_day_effective_date ||
    (user?.work_hours_per_day_history?.length ?? 0) > 0;
  const backendHasDepartment =
    user?.department_effective_date ||
    (user?.user_department_history?.length ?? 0) > 0;

  if (backendHasFiredDate && backendHasWorkHours && backendHasDepartment) {
    clearPendingEffectiveDates(userId);
    return formData;
  }

  return {
    ...formData,
    firedDate:
      formData.firedDate || normalizeEffectiveDate(pending.is_fired_date),
    workHoursPerDayEffectiveDate:
      formData.workHoursPerDayEffectiveDate ||
      normalizeEffectiveDate(pending.work_hours_per_day_effective_date),
    departmentEffectiveDate:
      formData.departmentEffectiveDate ||
      normalizeEffectiveDate(pending.department_effective_date),
  };
};

/** Подставляет в formData даты из PUT, если GET их не вернул */
export const mergeSentEffectiveDates = (formData, sentPayload) => {
  if (!sentPayload) return formData;
  const pick = (formKey, sentKey) => {
    const fromSent = sentPayload[sentKey];
    if (fromSent) return normalizeEffectiveDate(fromSent);
    return formData[formKey] || "";
  };
  return {
    ...formData,
    firedDate: pick("firedDate", "is_fired_date"),
    salaryEffectiveDate: pick("salaryEffectiveDate", "salary_effective_date"),
    workHoursPerDayEffectiveDate: pick(
      "workHoursPerDayEffectiveDate",
      "work_hours_per_day_effective_date",
    ),
    companyIdEffectiveDate: pick(
      "companyIdEffectiveDate",
      "company_id_effective_date",
    ),
    departmentEffectiveDate: pick(
      "departmentEffectiveDate",
      "department_effective_date",
    ),
  };
};
